/* workspace-admin/Detail — Workspace overview (admin). Clean ESM. */
import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { NexusIcon } from '../shared/ui/NexusIcon';
import { Btn, Pill, EmptyState } from '../shared/ui';
import { StatCard, PageHead } from '../platform/_ui';
import { findWorkspace, workspaceMembers } from '../platform/platformStore';
import { STATUS_LABEL, STATUS_COLOR } from './workspaceConstants.js';

export default function Detail({ data }) {
  const { wsId } = useParams();
  const navigate = useNavigate();
  const auth = window.useAuth();
  const [openingWs, setOpeningWs] = useState(false);
  const ws = findWorkspace(data.state, wsId);
  if (!ws) return null;

  const members = workspaceMembers(data.state, wsId);
  const admins = members.filter(m => m.wsRole === 'WORKSPACE_ADMIN');
  const org = data.state.organizations.find(o => o.id === ws.org);
  const isArchived = ws.status === 'ARCHIVED';
  const isSuspended = ws.status === 'SUSPENDED';
  let openBlockedReason = null;
  if (isArchived) openBlockedReason = 'Archived workspaces cannot be opened';
  else if (isSuspended) openBlockedReason = 'Suspended workspaces cannot be opened';

  const handleOpenCRM = async () => {
    setOpeningWs(true);
    try {
      if (auth?.switchWorkspace) await auth.switchWorkspace(ws.id);
      navigate('/app/' + ws.id);
    } catch {
      navigate('/app/' + ws.id);
    } finally {
      setOpeningWs(false);
    }
  };

  return (
    <div className="nx-cc-page">
      <PageHead
        title="Workspace Overview"
        sub={ws.name + (org ? ' · ' + org.name : '')}
        actions={
          <Btn variant="primary" icon="ArrowRight"
            disabled={openingWs || !!openBlockedReason}
            aria-label={openBlockedReason ?? 'Open workspace in CRM'}
            title={openBlockedReason ?? undefined}
            onClick={handleOpenCRM}>
            {openingWs ? 'Opening…' : 'Open in CRM'}
          </Btn>
        }
      />

      {isSuspended && (
        <div className="nx-banner nx-banner--warning" role="alert" style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 16px', marginBottom: 16, borderRadius: 6, background: 'var(--nx-amber-50, #fffbeb)', border: '1px solid var(--nx-amber-200, #fde68a)', color: 'var(--nx-amber-800, #92400e)' }}>
          <NexusIcon name="PauseCircle" size={16} />
          <span style={{ fontSize: 13 }}>This workspace is <strong>suspended</strong>. Members cannot access the CRM. Contact your organization admin to restore access.</span>
        </div>
      )}

      <div className="nx-cc-stat-grid">
        <StatCard label="Members"          value={ws.members} icon="Users"    color="blue"   onClick={() => navigate('/workspaces/' + wsId + '/members')} />
        <StatCard label="Workspace Admins" value={admins.length}                icon="Shield"   color="purple" />
        <StatCard label="Modules"          value={ws.modules.length}            icon="Layers"   color="green"  />
        <StatCard label="Status"           value={STATUS_LABEL[ws.status] ?? ws.status} icon="Activity" color={STATUS_COLOR[ws.status] ?? 'gray'} />
      </div>

      <div className="nx-panel" style={{ padding: 20, marginTop: 18, maxWidth: 720 }}>
        <h3 className="nx-panel-title" style={{ marginBottom: 12 }}>Enabled Modules</h3>
        {ws.modules.length === 0 ? (
          <EmptyState icon="Layers" title="No modules enabled" desc="Enable modules in workspace settings." />
        ) : (
          <ul style={{ display: 'flex', flexWrap: 'wrap', gap: 6, listStyle: 'none', margin: 0, padding: 0 }} aria-label="Enabled modules">
            {ws.modules.map(m => (
              <li key={m} className="nx-org-ws-module" style={{ fontSize: 12, padding: '4px 12px' }}>{m}</li>
            ))}
          </ul>
        )}
      </div>

      <div className="nx-panel" style={{ padding: 20, marginTop: 16, maxWidth: 720 }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
          <h3 className="nx-panel-title" style={{ flex: 1 }}>Workspace Admins</h3>
          <Btn variant="ghost" size="sm" icon="Users" aria-label="Manage workspace members" onClick={() => navigate('/workspaces/' + wsId + '/members')}>Manage Members</Btn>
        </div>
        {admins.length === 0 ? (
          <EmptyState icon="Shield" title="No workspace admins" desc="Assign an admin in the Membership tab." />
        ) : (
          <ul style={{ display: 'flex', flexDirection: 'column', gap: 8, listStyle: 'none', margin: 0, padding: 0 }} aria-label="Workspace admins">
            {admins.map(a => (
              <li key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span className="nx-domain-mark" aria-hidden="true" style={{ width: 26, height: 26, fontSize: 11, background: (a.member.tone || '#125AB0') + '22', color: a.member.tone }}>{a.member.name?.[0] ?? '?'}</span>
                <span style={{ flex: 1 }}>
                  <span className="nx-row-name-text">{a.member.name}</span>
                  <div className="nx-muted nx-mini">{a.member.email}</div>
                </span>
                <Pill tone="green">Workspace Admin</Pill>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
